import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Timeoff, TimeoffBalance, TimeoffRequest, TimeoffStateType } from "../../../types";
import {
    createTimeOffRequest,
    getBalanceByCategory,
    getTimeoffEarningCodes,
    getTimeoffPolicyList,
    getTimeoffRequestDetails,
    getTimeOffRequestList,
    updateTimeoffPolicy,
    updateTimeoffRequest,
} from "./api";

const initialState: TimeoffStateType = {
    fetchedTimeoffRequests: [],
    isLoadingTimeOffRequests: false,

    isUpdateTimeoffMode: false,

    isLoadingTimeoffPolicies: false,
    timeoffPolicies: [],

    isLoadingTimeoffEarningCodes: false,
    timeoffEarningCodes: [],

    isUpdateMode: false,
    isUpdatingTimeoff: false,

    isLoadingTodayTimeoffRequest: false,

    isLoadingTimeoffDetails: false,
    timeoffDetails: undefined,
};

const timeoffState = createSlice({
    name: "timeoff",
    initialState,
    reducers: {
        setRequestStartDate: (state, action) => {
            state.selectedRequestStartDate = action.payload;
        },

        setRequestEndDate: (state, action) => {
            state.selectedRequestEndDate = action.payload;
        },

        clearRequestDates: (state) => {
            state.selectedRequestStartDate = undefined;
            state.selectedRequestEndDate = undefined;
        },

        setSelectedTimeoffForView: (state, action) => {
            state.selectedTimeoffForView = action.payload;
        },

        clearSelectedTimeoffForView: (state) => {
            state.selectedTimeoffForView = undefined;
        },

        setTimeoffUpdateMode: (state, action: PayloadAction<boolean>) => {
            state.isUpdateTimeoffMode = action.payload;
        },

        clearTimeoffUpdateMode: (state) => {
            state.isUpdateTimeoffMode = false;
        },

        setTimeoffToUpdate: (state, action: PayloadAction<Timeoff>) => {
            state.timeoffToUpdate = action.payload;
        },

        clearTimeoffToUpdate: (state) => {
            state.timeoffToUpdate = undefined;
        },
    },
    extraReducers: {
        [getTimeOffRequestList.pending.type]: (state) => {
            state.isLoadingTimeOffRequests = true;
            state.fetchedTimeoffRequests = [];
        },

        [getTimeOffRequestList.fulfilled.type]: (state, { payload }) => {
            state.isLoadingTimeOffRequests = false;
            state.fetchedTimeoffRequests = payload.data;
        },

        [getTimeOffRequestList.rejected.type]: (state) => {
            state.isLoadingTimeOffRequests = false;
            state.fetchedTimeoffRequests = [];
        },

        [getBalanceByCategory.fulfilled.type]: (state, { payload }) => {
            state.timeOffBalanceByCategory = payload
                .map((item: TimeoffBalance) => {
                    return {
                        ...item,
                        max: parseFloat(item.max!).toFixed(2),
                    };
                })
                .filter((item: TimeoffBalance) => !isNaN(Number(item.max!)));
        },

        [getBalanceByCategory.rejected.type]: (state) => {
            state.timeOffBalanceByCategory = undefined;
        },

        [getBalanceByCategory.pending.type]: (state) => {
            state.timeOffBalanceByCategory = undefined;
        },

        [createTimeOffRequest.fulfilled.type]: (state) => {
            state.isCreatingTimeoffRequest = false;
        },

        [createTimeOffRequest.pending.type]: (state) => {
            state.isCreatingTimeoffRequest = true;
        },

        [createTimeOffRequest.rejected.type]: (state) => {
            state.isCreatingTimeoffRequest = false;
        },

        [updateTimeoffRequest.fulfilled.type]: (state) => {
            state.isUpdatingTimeoffRequest = false;
        },

        [updateTimeoffRequest.pending.type]: (state) => {
            state.isUpdatingTimeoffRequest = true;
        },

        [updateTimeoffRequest.rejected.type]: (state) => {
            state.isUpdatingTimeoffRequest = false;
        },

        [getTimeoffPolicyList.fulfilled.type]: (state, { payload }) => {
            state.isLoadingTimeoffPolicies = false;
            state.timeoffPolicies = payload;
        },

        [getTimeoffPolicyList.pending.type]: (state) => {
            state.isLoadingTimeoffPolicies = true;
            state.timeoffPolicies = [];
        },

        [getTimeoffPolicyList.rejected.type]: (state) => {
            state.isLoadingTimeoffPolicies = false;
            state.timeoffPolicies = [];
        },

        [getTimeoffEarningCodes.fulfilled.type]: (state, { payload }) => {
            state.isLoadingTimeoffEarningCodes = false;
            state.timeoffEarningCodes = payload;
        },

        [getTimeoffEarningCodes.pending.type]: (state) => {
            state.isLoadingTimeoffEarningCodes = true;
            state.timeoffEarningCodes = [];
        },

        [getTimeoffEarningCodes.rejected.type]: (state) => {
            state.isLoadingTimeoffEarningCodes = false;
            state.timeoffEarningCodes = [];
        },

        [updateTimeoffPolicy.fulfilled.type]: (state) => {
            state.isUpdatingTimeoff = false;
        },

        [updateTimeoffPolicy.pending.type]: (state) => {
            state.isUpdatingTimeoff = true;
        },

        [updateTimeoffPolicy.rejected.type]: (state) => {
            state.isUpdatingTimeoff = false;
        },

        [getTimeoffRequestDetails.fulfilled.type]: (state, action: PayloadAction<TimeoffRequest | undefined>) => {
            state.isLoadingTimeoffDetails = false;
            state.timeoffDetails = action.payload;
        },

        [getTimeoffRequestDetails.pending.type]: (state) => {
            state.isLoadingTimeoffDetails = true;
            state.timeoffDetails = undefined;
        },

        [getTimeoffRequestDetails.rejected.type]: (state) => {
            state.isLoadingTimeoffDetails = false;
            state.timeoffDetails = undefined;
        },
    },
});

export const {
    clearRequestDates,
    setRequestEndDate,
    setRequestStartDate,
    clearSelectedTimeoffForView,
    setSelectedTimeoffForView,
    clearTimeoffToUpdate,
    clearTimeoffUpdateMode,
    setTimeoffToUpdate,
    setTimeoffUpdateMode,
} = timeoffState.actions;
export default timeoffState.reducer;

//
